import { useJWT } from "../../../../hooks/react-query/useJWT";
import { Dispatch } from "react";
import { BACKEND_URL } from "../../../../constants/env";
import { useAlertStore } from "../../../config/store/alertStore";
import { useToastStore } from "../../../config/store/toastStore";
import { Appointment, FieldError } from "../../../../constants/model/models";
import {
  queryClient,
  QueryKeys,
} from "../../../../hooks/react-query/query-client";
import { useTranslation } from "next-i18next";
import { useProcessingStore } from "../../../config/store/usProccessingStore";

export function useAppointmentHelper() {
  const { jwt } = useJWT();
  const { setAlert } = useAlertStore();
  const { setToast } = useToastStore();
  const { t } = useTranslation("common");
  const { setProcessing } = useProcessingStore((state) => state);

  async function confirmCancellation(
    appointmentId: string,
    setProcessing: Dispatch<boolean>,
    closeModal: () => void
  ) {
    setProcessing(true);

    if (jwt) {
      try {
        const response = await cancelAppointment(appointmentId, jwt);

        if (response.status === 200) {
          await queryClient.invalidateQueries(QueryKeys.APPOINTMENTS);
          await queryClient.invalidateQueries(QueryKeys.STATS);
          setToast(t("success"), t("appointments.cancelAppSuccess"));

          closeModal();
        } else {
          const errorResponse = await response.json();
          if (errorResponse && errorResponse.errors) {
            const errors: FieldError[] = errorResponse.errors;
            if (errors.length > 0) {
              const error = errors[0];
              if (error.field === "appointmentId") {
                setAlert(t("appointments.invalidAppointment"));
              } else {
                setAlert(t("appointments.cancelGenericError"));
              }
            } else {
              setAlert(t("appointments.cancelGenericError"));
            }
          } else {
            setAlert(t("appointments.cancelGenericError"));
          }
        }

        setProcessing(false);
      } catch (err) {
        console.error(err);
        setProcessing(false);
        setAlert(t("appointments.cancelGenericError"));
      }
    }
  }

  async function cancelAppointment(appointmentId: string, jwt: string) {
    return await fetch(`${BACKEND_URL}/user/appointments`, {
      method: "PUT",
      headers: {
        "Content-type": "application/json",
        accessToken: jwt,
      },
      body: JSON.stringify({ appointmentId: appointmentId }),
    });
  }

  async function getPresenceDeclaration(appointmentId: string, jwt: string) {
    const response = await fetch(
      `${BACKEND_URL}/user/appointments/presence?appointmentId=${appointmentId}`,
      {
        method: "GET",
        headers: {
          responseType: "blob",
          accessToken: jwt,
        },
      }
    );

    return await response;
  }

  async function downloadPresenceDeclaration(appointment: Appointment) {
    setProcessing(true);
    try {
      if (jwt) {
        const response = await getPresenceDeclaration(appointment.id, jwt);

        if (response && response.status === 200) {
          // Create blob link to download
          const blob = await response.blob();
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "presence.pdf");

          // Append to html link element page
          document.body.appendChild(link);

          // Start download
          link.click();

          // Clean up and remove the link
          if (link.parentNode) link.parentNode.removeChild(link);
          setProcessing(false);
        } else {
          setAlert(t("downloadError"));
        }
      } else {
        setAlert(t("downloadError"));
        setProcessing(false);
      }
    } catch (err) {
      console.error(err);
      setAlert(t("downloadError"));
      setProcessing(false);
    }
  }

  return { confirmCancellation, downloadPresenceDeclaration };
}
